import * as React from 'react'
import {
    ChangeEventHandler,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import classnames from 'classnames'
import { Checkbox, CheckboxGroupProps } from '../Form'
import { useUniqueOptions } from '../../../hooks/useUniqueOptions/useUniqueOptions'
import { Option } from '../Form/types'
import { VirtualizedList } from '../../molecules/VirtualizedList/VirtualizedList'

export type FlatCheckboxGroupOptionType = {
    value: string
    label: React.ReactNode
    indeterminate?: boolean
}

const DEFAULT_ITEM_HEIGHT = 40
const DEFAULT_CONTAINER_HEIGHT = 400

type FlatCheckboxGroupProps = React.PropsWithChildren & {
    options: Array<FlatCheckboxGroupOptionType>
    checkedItems: string[]
    onChange: (newCheckedItems: string[]) => void
    checkboxLabelWrapperClassname?: string
    itemWrapperClassname?: string
    wrapperClassname?: string
    renderLabel?: CheckboxGroupProps['renderLabel']
    containerHeight?: number
}

type MeasuredCheckboxItemProps = {
    option: FlatCheckboxGroupOptionType
    isChecked: boolean
    onCheckboxChange: ChangeEventHandler<HTMLInputElement>
    itemWrapperClassname?: string
    checkboxLabelWrapperClassname?: string
    renderLabel?: CheckboxGroupProps['renderLabel']
    onHeightMeasured: (value: string, height: number) => void
}

const MeasuredCheckboxItem: React.FC<MeasuredCheckboxItemProps> = ({
    option,
    isChecked,
    onCheckboxChange,
    itemWrapperClassname,
    checkboxLabelWrapperClassname,
    renderLabel,
    onHeightMeasured,
}) => {
    const ref = useRef<HTMLDivElement>(null)
    const lastMeasuredHeight = useRef<number | null>(null)

    useEffect(() => {
        if (!ref.current) return undefined

        const measure = () => {
            const newHeight = ref.current ? ref.current.offsetHeight : 0
            if (newHeight && lastMeasuredHeight.current !== newHeight) {
                lastMeasuredHeight.current = newHeight
                onHeightMeasured(option.value, newHeight)
            }
        }

        measure()

        // Re-measure if the checkbox row resizes (e.g. label wraps differently)
        const resizeObserver = new ResizeObserver(measure)
        resizeObserver.observe(ref.current)

        return () => {
            resizeObserver.disconnect()
        }
    }, [onHeightMeasured, option.label, option.value])

    const wrapperClassname = classnames('grow', itemWrapperClassname)

    const label = renderLabel
        ? renderLabel(option as Option<string>)
        : option.label

    return (
        <div ref={ref}>
            <Checkbox
                indeterminate={option.indeterminate}
                name={`checkbox-${option.value}`}
                label={label}
                isBoxed
                checked={isChecked}
                onChange={onCheckboxChange}
                itemWrapperClassname={wrapperClassname}
                labelWrapperClassname={classnames(
                    'text-ellipsis overflow-hidden',
                    checkboxLabelWrapperClassname
                )}
            />
        </div>
    )
}

export const FlatCheckboxGroup: React.FC<FlatCheckboxGroupProps> = ({
    options,
    checkedItems,
    onChange,
    checkboxLabelWrapperClassname,
    itemWrapperClassname,
    wrapperClassname,
    renderLabel,
    containerHeight = DEFAULT_CONTAINER_HEIGHT,
}) => {
    // Deduplicate options by value, keeping the first occurrence
    const uniqueOptions = useUniqueOptions(options)

    const checkedSet = useMemo(() => new Set(checkedItems), [checkedItems])

    // Track measured heights for each item
    const [heights, setHeights] = useState<{ [key: string]: number }>({})

    const handleChange = useCallback(
        (value: string, isChecked: boolean) => {
            if (isChecked) {
                if (!checkedItems.includes(value)) {
                    onChange([...checkedItems, value])
                }
            } else {
                onChange(checkedItems.filter((item) => item !== value))
            }
        },
        [checkedItems, onChange]
    )

    const handleHeightMeasured = useCallback(
        (value: string, height: number) => {
            setHeights((prev) => {
                if (prev[value] === height) return prev
                return { ...prev, [value]: height }
            })
        },
        []
    )

    const renderItem = useCallback(
        (option: FlatCheckboxGroupOptionType) => {
            const isChecked = checkedSet.has(option.value)

            const handleCheckboxChange: ChangeEventHandler<HTMLInputElement> = (
                event
            ) => {
                handleChange(option.value, event.target.checked)
            }

            return (
                <MeasuredCheckboxItem
                    option={option}
                    isChecked={isChecked}
                    onCheckboxChange={handleCheckboxChange}
                    itemWrapperClassname={itemWrapperClassname}
                    checkboxLabelWrapperClassname={
                        checkboxLabelWrapperClassname
                    }
                    renderLabel={renderLabel}
                    onHeightMeasured={handleHeightMeasured}
                />
            )
        },
        [
            checkedSet,
            handleChange,
            itemWrapperClassname,
            checkboxLabelWrapperClassname,
            renderLabel,
            handleHeightMeasured,
        ]
    )

    const getItemHeight = useCallback(
        (option: FlatCheckboxGroupOptionType) =>
            heights[option.value] ?? DEFAULT_ITEM_HEIGHT,
        [heights]
    )

    const totalHeight = useMemo(() => {
        if (!uniqueOptions || uniqueOptions.length === 0) return 0
        return uniqueOptions.reduce(
            (sum, option) => sum + getItemHeight(option),
            0
        )
    }, [uniqueOptions, getItemHeight])

    const effectiveContainerHeight =
        totalHeight > 0
            ? Math.min(totalHeight, containerHeight)
            : containerHeight

    if (!uniqueOptions || uniqueOptions.length === 0) {
        return <div className={wrapperClassname} />
    }

    return (
        <div className={wrapperClassname}>
            <VirtualizedList
                items={uniqueOptions}
                containerHeight={effectiveContainerHeight}
                renderItem={renderItem}
                getItemHeight={getItemHeight}
                getItemKey={(option) => option.value}
            />
        </div>
    )
}
